/**
 * Hooks React Query pour la gestion des classes scolaires
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { classesClient } from '../api';
import type {
  Class,
  CreateClassDto,
  UpdateClassDto,
  LightClass,
} from '@sphere/school/domain';
import { IOffsetPaginationResponse, SortOrder } from '@sphere/shared/domain';

export const classKeys = {
  all: ['classes'] as const,
  lists: () => [...classKeys.all, 'list'] as const,
  list: (filters?: { establishmentId?: string }) =>
    [...classKeys.lists(), filters] as const,
  details: () => [...classKeys.all, 'detail'] as const,
  detail: (id: string) => [...classKeys.details(), id] as const,
  batch: (classIds: string[]) => [...classKeys.all, 'batch', classIds] as const,
};

/**
 * Hook pour lister les classes
 * Le backend filtre automatiquement selon le scope utilisateur
 */
export function useClasses(
  params?: {
    establishmentId?: string;
    levelExternalId?: string;
    sectionId?: string;
    statusId?: string;
    schoolYearId?: string;
    page?: number;
    limit?: number;
    sort?: string;
    order?: SortOrder;
    include?: string;
    q?: string;
  },
  options?: Omit<
    UseQueryOptions<IOffsetPaginationResponse<Class>>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery<IOffsetPaginationResponse<Class>>({
    queryKey: classKeys.list(params),
    queryFn: () => classesClient.list(params),
    ...options,
  });
}

/**
 * Hook pour récupérer plusieurs classes en mode léger (id + nom uniquement).
 * Une seule requête batch au lieu de N appels getById.
 */
export function useClassesBatch(
  classIds: string[] | undefined,
  options?: Omit<
    UseQueryOptions<LightClass[]>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery<LightClass[]>({
    queryKey: classKeys.batch(classIds ?? []),
    queryFn: () => classesClient.getBatchLight(classIds!),
    enabled: !!classIds && classIds.length > 0,
    ...options,
  });
}

/**
 * Hook pour récupérer une classe par ID
 */
export function useClass(
  id: string | undefined,
  options?: Omit<UseQueryOptions<Class>, 'queryKey' | 'queryFn'>
) {
  return useQuery<Class>({
    queryKey: classKeys.detail(id!),
    queryFn: () => classesClient.getById(id!),
    enabled: !!id,
    ...options,
  });
}

/**
 * Hook pour créer une classe
 */
export function useCreateClass() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: CreateClassDto) => classesClient.create(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: classKeys.all });
    },
  });
}

/**
 * Hook pour mettre à jour une classe
 */
export function useUpdateClass() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: UpdateClassDto }) =>
      classesClient.update(id, dto),
    onSuccess: (
      _data: Class,
      variables: { id: string; dto: UpdateClassDto }
    ) => {
      queryClient.invalidateQueries({ queryKey: classKeys.all });
      queryClient.invalidateQueries({
        queryKey: classKeys.detail(variables.id),
      });
    },
  });
}

/**
 * Hook pour vérifier si l'utilisateur peut créer une classe
 * @param establishmentId - ID de l'établissement (optionnel)
 * @param options - Options React Query
 */
export function useCanCreateClass(
  establishmentId?: string,
  options?: Omit<UseQueryOptions<boolean>, 'queryKey' | 'queryFn'>
) {
  return useQuery<boolean>({
    queryKey: [...classKeys.all, 'canCreate', establishmentId] as const,
    queryFn: () => classesClient.canCreate(establishmentId),
    ...options,
  });
}

/**
 * Hook pour supprimer une classe
 */
export function useDeleteClass() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => classesClient.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: classKeys.all });
    },
  });
}

/**
 * Hook pour ajouter des membres à une classe
 */
export function useAddClassMembers(classId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userId,
      roleCode,
    }: {
      userId: string;
      roleCode: string;
    }) => classesClient.addMembers(classId, [userId], roleCode),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['directory', 'classes', classId, 'members'],
      });
      queryClient.invalidateQueries({ queryKey: classKeys.all });
    },
  });
}

/**
 * Hook pour retirer un membre d'une classe
 */
export function useRemoveClassMember(classId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (membershipId: string) =>
      classesClient.removeMember(membershipId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['directory', 'classes', classId, 'members'],
      });
      queryClient.invalidateQueries({ queryKey: classKeys.all });
    },
  });
}
